import { FC } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	LinearProgress,
	Typography,
} from '@mui/material';
import { TPgRecurringRun } from 'services/api/paymentGateway/types';
import { PG_RUN_KIND_LABELS, PG_RUN_STATUS_LABELS } from '../constants';
import { formatDate, formatDateTime } from '../utils';
import { RunStats } from './RunStats';
import { useRecurringRun } from './useRecurringRun';

type TRunDetailsDialogProps = {
	runId: string | null;
	onClose: () => void;
};

const STATUS_COLOR = { running: 'info', completed: 'success', failed: 'error' } as const;

/** Run progress and counters; shared with the "Run now" dialog. */
export const RunSummary: FC<{ run: TPgRecurringRun }> = ({ run }) => {
	const progress = run.customersTotal > 0 ? (run.customersDone / run.customersTotal) * 100 : 0;
	return (
		<Box>
			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
				<Chip
					size="small"
					label={PG_RUN_STATUS_LABELS[run.status] ?? run.status}
					color={STATUS_COLOR[run.status] ?? 'default'}
				/>
				<Chip size="small" variant="outlined" label={PG_RUN_KIND_LABELS[run.kind] ?? run.kind} />
				{run.dryRun && <Chip size="small" color="warning" label="Dry run — nothing charged" />}
			</Box>
			<Grid container spacing={1} sx={{ mb: 1 }}>
				{[
					['Run date', formatDate(run.runDate)],
					['Started', formatDateTime(run.startedAt)],
					['Finished', formatDateTime(run.finishedAt)],
					['Triggered by', run.triggeredBy],
					['Customers', `${run.customersDone} of ${run.customersTotal}`],
				].map(([label, value]) => (
					<Grid item xs={12} sm={6} md={4} key={label}>
						<Typography variant="caption" color="text.secondary" component="div">
							{label}
						</Typography>
						<Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
							{value}
						</Typography>
					</Grid>
				))}
			</Grid>
			{run.status === 'running' && (
				<LinearProgress
					variant={run.customersTotal > 0 ? 'determinate' : 'indeterminate'}
					value={progress}
					sx={{ mb: 1 }}
				/>
			)}
			{run.error && (
				<Alert severity="error" sx={{ mb: 1 }}>
					{run.error}
				</Alert>
			)}
			<Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
				Counters
			</Typography>
			<RunStats stats={run.stats} />
		</Box>
	);
};

export const RunDetailsDialog: FC<TRunDetailsDialogProps> = ({ runId, onClose }) => {
	const { run, error } = useRecurringRun(runId);

	return (
		<Dialog open={!!runId} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle color="primary">Run {runId}</DialogTitle>
			<DialogContent>
				{error && (
					<Alert severity="error" sx={{ mb: 1 }}>
						{error}
					</Alert>
				)}
				{run ? <RunSummary run={run} /> : !error && <CircularProgress size={24} />}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Close</Button>
			</DialogActions>
		</Dialog>
	);
};
